import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, Loader2 } from "lucide-react";
import { http, DIVISIONS } from "../lib/api";
import { PageHero } from "../components/site/Motion";
import { AssetCard } from "../components/site/AssetCard";
import { SourcingNotice } from "../components/site/SourcingNotice";
import { FilterPanel } from "../components/inventory/FilterPanel";
import { SourcingHub } from "../components/site/SourcingService";

const KEYS = ["q", "division", "category", "make", "model", "condition", "location", "status", "min_price", "max_price", "min_year", "max_year", "sort"];

export default function Inventory({ fixedDivision = null }) {
  const [params, setParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const division = fixedDivision || DIVISIONS.find((d) => d.key === params.get("division"));
  const query = Object.fromEntries(KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
  if (fixedDivision) query.division = fixedDivision.key;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["assets", query],
    queryFn: async () => (await http.get("/assets", { params: query })).data,
    retry: 1,
  });
  const { data: facets } = useQuery({ queryKey: ["facets"], queryFn: async () => (await http.get("/assets/facets")).data, retry: 1 });
  const items = Array.isArray(data?.items) ? data.items : [];
  const filtered = KEYS.some((k) => k !== "division" && k !== "sort" && params.get(k));
  const noInventory = !fixedDivision && facets !== undefined && (facets?.total ?? 0) === 0;

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const clear = () => {
    setQ("");
    setParams(division && !fixedDivision ? { division: division.key } : {}, { replace: true });
  };

  return (
    <div data-testid={fixedDivision ? `division-inventory-${fixedDivision.key}` : "inventory-page"}>
      {!fixedDivision && <PageHero eyebrow={division ? `${division.name} · Inventory` : "Inventory · Asset Sourcing"} title={division ? [division.name.split(" ")[0], "inventory."] : ["Browse inventory,", "or let us source it."]}
        intro={division ? division.blurb : "Browse verified listings across our four divisions, or tell us what you need and we'll help you source it."} testid="inventory-hero" />}

      {noInventory ? (
        <div className="max-w-7xl mx-auto px-5 md:px-10 pb-24"><SourcingHub /></div>
      ) : (<>

      <div className={`${fixedDivision ? "" : "sticky top-[68px] z-30"} bg-[#0a0d14]/90 backdrop-blur-xl border-b hairline`}>
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex flex-col lg:flex-row gap-4 lg:items-center">
          {fixedDivision ? <p className="eyebrow">{fixedDivision.name} inventory</p> : (
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="division-tabs">
            {[{ key: "", short: "All" }, ...DIVISIONS].map((d) => {
              const active = (params.get("division") || "") === d.key;
              const n = d.key ? facets?.counts?.[d.key] : facets?.total;
              return (
                <button key={d.key || "all"} onClick={() => set({ division: d.key, category: "" })}
                  className={`shrink-0 px-4 py-2 text-sm border transition-colors duration-300 ${active ? "bg-[var(--gold)] text-[#0a0d14] border-[var(--gold)]" : "border-white/15 text-slate-200 hover:border-[var(--gold)]"}`}
                  data-testid={`division-tab-${d.key || "all"}`}>
                  {d.short}{n !== undefined && <span className="font-mono text-[0.65rem] ml-2 opacity-80">{n}</span>}
                </button>
              );
            })}
          </div>)}
          <form className="flex gap-2 lg:ml-auto lg:w-[420px]" onSubmit={(e) => { e.preventDefault(); set({ q: q.trim() }); }}>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="field !py-2.5 !pl-10" placeholder="Search make, model, stock #…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventory-search-input" />
            </div>
            <button type="submit" className="btn-gold !py-2.5 !px-4" data-testid="inventory-search-button">Search</button>
            <button type="button" onClick={() => setShowFilters((s) => !s)} className="lg:hidden btn-ghost !py-2.5 !px-3" aria-label="Filters" data-testid="mobile-filters-toggle"><SlidersHorizontal className="w-4 h-4" /></button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block lg:sticky lg:top-44 self-start border hairline p-6 bg-[var(--ink-2)]`}>
          <FilterPanel params={params} set={set} clear={clear} facets={facets} fixedDivision={fixedDivision} />
        </aside>
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-sm text-slate-300" data-testid="inventory-result-count">
              {isLoading ? "Loading…" : `${items.length} listing${items.length === 1 ? "" : "s"}`}
            </p>
            <select className="field !w-auto !py-2" value={params.get("sort") || "newest"} onChange={(e) => set({ sort: e.target.value === "newest" ? "" : e.target.value })} data-testid="inventory-sort-select">
              <option value="newest">Newest first</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
              <option value="year_desc">Year: newest</option>
              <option value="year_asc">Year: oldest</option>
            </select>
          </div>
          {isLoading ? (
            <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : items.length ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-grid">
              {items.map((a, i) => <AssetCard key={a.id} asset={a} index={i} />)}
            </div>
          ) : (
            <div className="space-y-6">
              {filtered && !isError && <p className="text-slate-300" data-testid="no-results-message">No listings match these filters.</p>}
              <SourcingNotice division={division} testid="inventory-sourcing-notice" />
            </div>
          )}
        </div>
      </div>
      {!fixedDivision && <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 border-t hairline"><SourcingHub hasListings /></div>}
      </>)}
    </div>
  );
}
